import { defineStore } from "pinia";
import { animalsService } from "@/api/services/esupplyvet/animalsService";

export const useAnimalsStore = defineStore("animals", {
  state: () => ({
    animals: [],
    animalTypes: [],
    animalRaces: [],
    loading: false,
    error: null,
  }),

  getters: {
    /**
     * Retourne la liste des animaux avec formatage pour l'affichage
     */
    formattedAnimals: state => {
      return state.animals.map(animal => {
        const animalType = state.animalTypes.find(type => type.id === animal.animalTypeId);
        const typeName = animalType ? animalType.name : "Type inconnu";

        return {
          ...animal,
          displayName: `${animal.name} (${typeName})`,
          formattedDate: animal.birthDate ? new Date(animal.birthDate).toLocaleDateString("fr-FR") : null,
          formattedWeight: animal.weight ? `${animal.weight}kg` : null,
          nextReminder: animal.nextVaccination
            ? `Prochain vaccin: ${new Date(animal.nextVaccination).toLocaleDateString("fr-FR")}`
            : null,
        };
      });
    },
    hasAnimals: state => state.animals.length > 0,
    /**
     * Retourne les races filtrées par type d'animal
     * @param {number} animalTypeId - ID du type d'animal
     */
    getRacesByAnimalType: state => animalTypeId => {
      if (!animalTypeId) return [];
      return state.animalRaces.filter(race => race.animalTypeId == animalTypeId);
    },
  },

  actions: {
    /**
     * Récupère la liste des animaux depuis l'API
     */
    async fetchAnimals() {
      this.loading = true;
      this.error = null;

      try {
        if (this.animalTypes.length === 0) {
          const [animalsData, animalTypesData] = await Promise.all([
            animalsService.getAnimals(),
            animalsService.getAnimalTypes(),
          ]);
          this.animals = animalsData;
          this.animalTypes = animalTypesData;
        } else {
          const data = await animalsService.getAnimals();
          this.animals = data;
        }
      } catch (error) {
        this.error = "Erreur lors du chargement des animaux";
        console.error("Erreur fetchAnimals:", error);
      } finally {
        this.loading = false;
      }
    },

    /**
     * Récupère la liste des types d'animaux
     */
    async fetchAnimalTypes() {
      try {
        const data = await animalsService.getAnimalTypes();
        this.animalTypes = data;
      } catch (error) {
        console.error("Erreur fetchAnimalTypes:", error);
        throw error;
      }
    },

    /**
     * Récupère la liste des races d'animaux
     */
    async fetchAnimalRaces() {
      try {
        const data = await animalsService.getAnimalRaces();
        this.animalRaces = data;
      } catch (error) {
        console.error("Erreur fetchAnimalRaces:", error);
        throw error;
      }
    },

    /**
     * Récupère un animal par son ID
     * @param {number|string} id - L'ID de l'animal à récupérer
     */
    async getAnimal(id) {
      try {
        const data = await animalsService.getAnimal(id);
        return data;
      } catch (error) {
        this.error = "Erreur lors de la récupération de l'animal";
        console.error("Erreur getAnimal:", error);
        throw error;
      }
    },

    /**
     * Met à jour un animal par son ID
     * @param {number|string} id - L'ID de l'animal à mettre à jour
     * @param {Object} animalData - Les données de l'animal à mettre à jour
     */
    async updateAnimal(id, animalData) {
      try {
        const updatedAnimal = await animalsService.updateAnimal(id, animalData);
        const index = this.animals.findIndex(animal => animal.id === id);
        if (index !== -1) {
          this.animals[index] = updatedAnimal;
        }
        return updatedAnimal;
      } catch (error) {
        this.error = "Erreur lors de la modification de l'animal";
        console.error("Erreur updateAnimal:", error);
        throw error;
      }
    },

    /**
     * Crée un nouvel animal
     * @param {Object} animalData - Les données de l'animal à créer
     */
    async createAnimal(animalData) {
      try {
        const newAnimal = await animalsService.createAnimal(animalData);
        this.animals.push(newAnimal);
        return newAnimal;
      } catch (error) {
        this.error = "Erreur lors de la création de l'animal";
        console.error("Erreur createAnimal:", error);
        throw error;
      }
    },

    /**
     * Supprime un animal par son ID
     * @param {number|string} id - L'ID de l'animal à supprimer
     */
    async deleteAnimal(id) {
      try {
        await animalsService.deleteAnimal(id);
        this.animals = this.animals.filter(animal => animal.id !== id);
      } catch (error) {
        this.error = "Erreur lors de la suppression de l'animal";
        console.error("Erreur deleteAnimal:", error);
        throw error;
      }
    },

    /**
     * Remet à zéro le store
     */
    resetAnimals() {
      this.animals = [];
      this.animalTypes = [];
      this.loading = false;
      this.error = null;
    },
  },
});
